'use client';

import { Check, Play } from 'lucide-react';
import type { ProductVariant } from '@/lib/data/software';

interface VariantTableProps {
  variants: ProductVariant[];
  selectedVariantId?: string;
  onSelectVariant?: (variant: ProductVariant) => void;
  selectedAddonIds?: string[];
  onToggleAddon?: (variant: ProductVariant) => void;
  showSelection?: boolean;
}

export default function VariantTable({
  variants,
  selectedVariantId,
  onSelectVariant,
  showSelection = true,
}: VariantTableProps) {
  if (variants.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No plans available.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200">
            {showSelection && <th className="w-12 px-4 py-3"></th>}
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
              Plan
            </th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
              Description
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {variants.map((variant) => {
            const isSelected = selectedVariantId === variant.id;

            return (
              <tr
                key={variant.id}
                className={`${
                  showSelection && onSelectVariant
                    ? 'cursor-pointer hover:bg-blue-50'
                    : ''
                } ${isSelected ? 'bg-blue-50' : ''}`}
                onClick={() => onSelectVariant?.(variant)}
              >
                {showSelection && (
                  <td className="px-4 py-4">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        isSelected
                          ? 'border-blue-600 bg-blue-600'
                          : 'border-gray-300'
                      }`}
                    >
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </td>
                )}
                <td className="px-4 py-4">
                  <span className="font-medium text-gray-900">
                    {variant.displayName}
                  </span>
                  {variant.isAddon && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
                      Add-on
                    </span>
                  )}
                  {variant.videoUrl && (
                    <a
                      href={variant.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      <Play className="w-3 h-3" />
                      Demo
                    </a>
                  )}
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-gray-600">
                    {variant.description || '—'}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Alternative card-based variant display for mobile
export function VariantCards({
  variants,
  selectedVariantId,
  onSelectVariant,
  selectedAddonIds = [],
  onToggleAddon,
  showSelection = true,
}: VariantTableProps) {
  if (variants.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 text-base">
        No plans available.
      </div>
    );
  }

  // Separate main plans from add-ons
  const mainPlans = variants.filter((v) => !v.isAddon);
  const addons = variants.filter((v) => v.isAddon);

  // Auto-determine recommended plan: middle plan for 3+, second for 2
  const recommendedIndex = mainPlans.length >= 3
    ? Math.floor(mainPlans.length / 2)
    : mainPlans.length === 2 ? 1 : -1;

  const renderPlanCard = (variant: ProductVariant, index: number) => {
    const isSelected = selectedVariantId === variant.id;
    const isRecommended = index === recommendedIndex;

    return (
      <div
        key={variant.id}
        role={showSelection && onSelectVariant ? 'button' : undefined}
        tabIndex={showSelection && onSelectVariant ? 0 : undefined}
        className={`p-4 sm:p-5 rounded-xl border-2 transition-colors min-h-[72px] relative ${
          showSelection && onSelectVariant ? 'cursor-pointer active:bg-blue-100' : ''
        } ${
          isSelected
            ? 'border-blue-600 bg-blue-50'
            : isRecommended
              ? 'border-blue-400 hover:border-blue-500'
              : 'border-gray-200 hover:border-blue-300'
        }`}
        onClick={() => onSelectVariant?.(variant)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelectVariant?.(variant);
          }
        }}
      >
        {isRecommended && (
          <span className="absolute -top-3 left-4 inline-flex items-center px-2.5 py-0.5 bg-blue-600 text-white text-xs font-semibold rounded-full">
            Most Popular
          </span>
        )}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-900 text-base">
              {variant.displayName}
            </h4>
            {variant.description && (
              <p className="mt-1.5 text-sm sm:text-base text-gray-600">
                {variant.description}
              </p>
            )}
            {variant.videoUrl && (
              <a
                href={variant.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                tabIndex={-1}
                className="mt-2 py-1 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
              >
                <Play className="w-3.5 h-3.5" />
                Watch Demo
              </a>
            )}
          </div>
          {showSelection && (
            <div
              className={`w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center mt-0.5 ${
                isSelected
                  ? 'border-blue-600 bg-blue-600'
                  : 'border-gray-300'
              }`}
            >
              {isSelected && <Check className="w-4 h-4 text-white" />}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAddonCard = (variant: ProductVariant) => {
    const isChecked = selectedAddonIds.includes(variant.id);

    return (
      <div
        key={variant.id}
        role={showSelection && onToggleAddon ? 'button' : undefined}
        tabIndex={showSelection && onToggleAddon ? 0 : undefined}
        className={`p-4 sm:p-5 rounded-xl border-2 transition-colors min-h-[72px] ${
          showSelection && onToggleAddon ? 'cursor-pointer active:bg-amber-100' : ''
        } ${
          isChecked
            ? 'border-amber-500 bg-amber-50'
            : 'border-gray-200 hover:border-amber-300'
        }`}
        onClick={() => onToggleAddon?.(variant)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleAddon?.(variant);
          }
        }}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-gray-900 text-base">
                {variant.displayName}
              </h4>
              <span className="inline-flex items-center px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
                Add-on
              </span>
            </div>
            {variant.description && (
              <p className="mt-1.5 text-sm sm:text-base text-gray-600">
                {variant.description}
              </p>
            )}
            {variant.videoUrl && (
              <a
                href={variant.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                tabIndex={-1}
                className="mt-2 py-1 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
              >
                <Play className="w-3.5 h-3.5" />
                Watch Demo
              </a>
            )}
          </div>
          {showSelection && (
            <div
              className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center mt-0.5 ${
                isChecked
                  ? 'border-amber-600 bg-amber-600'
                  : 'border-gray-300'
              }`}
            >
              {isChecked && <Check className="w-3.5 h-3.5 text-white" />}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Main Plans */}
      {mainPlans.map((plan, index) => renderPlanCard(plan, index))}

      {/* Add-ons Section */}
      {addons.length > 0 && (
        <>
          <div className="pt-4 pb-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <div className="h-px bg-gray-200 flex-1" />
              <span>Available Add-ons</span>
              <div className="h-px bg-gray-200 flex-1" />
            </div>
            <p className="text-xs text-gray-600 text-center mt-1">
              Add to any plan above
            </p>
          </div>
          {addons.map(renderAddonCard)}
        </>
      )}
    </div>
  );
}
